<template>
  <ReaderDrawer v-model="menuOpen">
    <template #header>
      <h2 class="font-medium text-lg">
        {{ t(key("header")) }}
      </h2>
    </template>
    <div v-if="paragraphId" class="mt-4 px-4">
      <NamiButton
        block
        button-type="secondary"
        @buttonClick="paragraphId = undefined"
      >
        {{ t(key("button.back_to_chapter_comments")) }}
      </NamiButton>
    </div>
    <FeedComments
      class="p-4 pb-8"
      :feed-id="paragraphId || chapterId"
      :feed-type="paragraphId ? 'novel_chapter_paragraph' : 'novel_chapter'"
      :highlightAuthorIds="orgMembers || undefined"
      :highlightAuthorTag="t(generic('tag.creator'))"
      :disable-fetch="!menuOpen"
      @comment="(v) => emit('comment', v)"
    />
  </ReaderDrawer>
</template>

<script setup lang="ts">
import {
  type CommentEntity,
  getId,
  isCreator,
  isMember,
  isOrg,
  isUser,
  type TitleEntity,
} from "~/src/api";
import { useQuery } from "@tanstack/vue-query";
import type { NovelSettingsInterface } from "~/src/novel/reader";

const { t } = useI18n();
const key = useI18nKey("components.novel.drawer.novelDrawerComments");
const generic = useI18nKey("generic");

const { chapterId, titleEntity } = defineProps<{
  chapterId: string;
  titleEntity?: TitleEntity;
  solo?: boolean;
}>();
const emit = defineEmits<{
  (e: "comment", v: CommentEntity): void;
}>();

const paragraphId = defineModel<string | undefined>("paragraphId");
const secondaryMenuOpen = defineModel<
  NovelSettingsInterface["secondaryMenuOpen"]
>("secondaryMenuOpen", { default: false });

const menuOpen = computed({
  get: () => secondaryMenuOpen.value === "comments",
  set: (v) => {
    if (v) secondaryMenuOpen.value = "comments";
    else secondaryMenuOpen.value = false;
  },
});

const orgId = computed(() => titleEntity?.relationships.find(isOrg)?.id ?? "");

const { data: orgMembers } = useQuery({
  ...useOrganizationGet(orgId),
  select: (v) =>
    v.relationships
      .filter((rel) => isUser(rel) || isCreator(rel) || isMember(rel))
      .map(getId)
      .concat([orgId.value]),
});
</script>

<style scoped lang="postcss"></style>
